import { BillingDetails } from '@/models/Billing';
import { Amount } from '@/models/Price';
import { TimeWindow } from '@/models/TimeWindow';

export type InvoiceStatus = 'pending' | 'succeeded' | 'canceled';
export type InvoiceLineProduct =
  | 'free_plan'
  | 'store_subscription_fee'
  | 'supplier_subscription_fee'
  | 'supplier_order_fee'
  | 'retailer_subscription_fee';

export type InvoiceTransactionStatus =
  | 'pending'
  | 'processing'
  | 'succeeded'
  | 'failed'
  | 'canceled';

export interface InvoiceLine {
  product: InvoiceLineProduct;
  quantity: number;
  vatRate?: number;
  unitPrice: Amount;
  lineItemPrice: Amount;
  vatAmount: Amount;
  totalAmount: Amount;
  orderIds?: string[];
}

export interface Invoice {
  id: string;
  invoiceNumber: string;
  description: string;
  subscriptionId: string;
  status: InvoiceStatus;
  paymentStatus?: InvoiceTransactionStatus | null;
  period: TimeWindow;
  subtotalAmount: Amount;
  taxAmount: Amount;
  totalAmount: Amount;
  lines: InvoiceLine[];
  billingDetails: BillingDetails;
  createdAt: Date;
  updatedAt: Date;
}

export interface Invoices {
  totalCount?: number;
  estimatedTotalCount: number;
  items: Invoice[];
}

export interface InvoiceTransaction {
  id: string;
  invoiceId: string;
  paymentMethodId?: string | null;
  provider?: string | null;
  providerCustomerId?: string | null;
  providerPaymentIntentId?: string | null;
  providerChargeId?: string | null;
  status: InvoiceTransactionStatus;
  amount: Amount;
  failureCode?: string | null;
  failureMessage?: string | null;
  attemptedAt: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface InvoiceTransactions {
  totalCount?: number;
  estimatedTotalCount: number;
  items: InvoiceTransaction[];
}

export interface InvoiceUpdate {
  description: string;
  subtotalAmount: Amount;
  taxAmount: Amount;
  totalAmount: Amount;
  billingDetails?: BillingDetails;
}

export interface GeneratedInvoices {
  generated: number;
}

export interface ProcessedInvoiceTransactions {
  totalCount?: number;
  estimatedTotalCount: number;
  processed: number;
}

const INVOICE_LINE_PRODUCT_LABELS: Record<InvoiceLineProduct, string> = {
  free_plan: 'Free plan',
  store_subscription_fee: 'Store subscription fee',
  supplier_subscription_fee: 'Supplier subscription fee',
  supplier_order_fee: 'Supplier order fee',
  retailer_subscription_fee: 'Retailer subscription fee',
};

export function formatInvoiceLineProduct(product: InvoiceLineProduct): string {
  return INVOICE_LINE_PRODUCT_LABELS[product];
}
